import React from 'react';
import { motion } from 'framer-motion';
import { Star, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps/search/?api=1&query=Caba%C3%B1as+Clarita+Dina+Huapi';
interface Review {
  name: string;
  initial: string;
  date: string;
  rating: number;
  text: string;
}
function Stars({
  rating,
  className = ''
}: {
  rating: number;
  className?: string;
}) {
  return <span className={`flex items-center gap-0.5 ${className}`} aria-hidden="true" data-id="element-278" data-mp="2w7sbby1xwro1">
      {Array.from({
      length: 5
    }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} data-id="element-279" data-mp="351okmca0a9m1" data-mpi={i} />)}
    </span>;
}
export function Reviews() {
  const {
    t
  } = useLanguage();
  const average = '4,2';
  const reviews: Review[] = [{
    name: 'Marcela G.',
    initial: 'M',
    date: t('hace 2 meses', '2 months ago'),
    rating: 5,
    text: t('Excelente atención de la familia. La cabaña impecable, muy cálida y con todo lo necesario. A pocos minutos de Bariloche y en una zona muy tranquila. Volvemos seguro.', 'Excellent service from the family. The cabin was spotless, very warm and had everything we needed. A few minutes from Bariloche and in a very quiet area. We will definitely be back.')
  }, {
    name: 'Juan Pablo R.',
    initial: 'J',
    date: t('hace 4 meses', '4 months ago'),
    rating: 5,
    text: t('Fuimos por la pesca y superó las expectativas. El quincho con parrilla es un plus enorme y la calefacción funciona perfecto incluso con mucho frío.', 'We came for the fishing and it exceeded our expectations. The quincho with the grill is a huge plus and the heating works perfectly even in very cold weather.')
  }, {
    name: 'Vanina L.',
    initial: 'V',
    date: t('hace 6 meses', '6 months ago'),
    rating: 5,
    text: t('Muy buena relación precio-calidad. Nos alojamos con los chicos en la cabaña de dos habitaciones y estuvimos muy cómodos. Estacionamiento dentro del predio.', 'Very good value for money. We stayed with the kids in the two-bedroom cabin and were very comfortable. Parking inside the property.')
  }, {
    name: 'Diego A.',
    initial: 'D',
    date: t('hace 8 meses', '8 months ago'),
    rating: 4,
    text: t('Lugar tranquilo y muy limpio. La ubicación en Dina Huapi es ideal para recorrer la zona en auto. Buen wifi para trabajar unos días.', 'Quiet and very clean place. The location in Dina Huapi is ideal for exploring the area by car. Good Wi-Fi for working a few days.')
  }];
  return <section id="resenas" className="py-24 bg-white" data-id="element-280" data-mp="2nuqa643gdxca">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-281" data-mp="2nw1cs0l5wz1t">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start" data-id="element-282" data-mp="2sucn0cfddbkk">
          {/* Resumen */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-4 lg:sticky lg:top-28" data-id="element-283" data-mp="h5pqfgoq4hu5">
            <span className="text-accent font-medium tracking-wider uppercase text-sm mb-3 block" data-id="element-284" data-mp="m4j0ez4sxqpk">
              {t('Reseñas', 'Reviews')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-primary mb-6" data-id="element-285" data-mp="vvdom91lz0dn">
              {t('Lo que dicen nuestros huéspedes', 'What our guests say')}
            </h2>

            <div className="flex items-end gap-4 mb-3" data-id="element-286" data-mp="19udbpc9h377p">
              <span className="font-serif text-6xl leading-none text-primary tabular-nums" data-id="element-287" data-mp="14vsdfids6xlw">
                {average}
              </span>
              <div className="pb-1" data-id="element-288" data-mp="3bmrt8a83u9nl">
                <Stars rating={4} data-id="element-289" data-mp="36nonsoo7q1f3" />
                <p className="text-sm text-gray-500 mt-1" data-id="element-290" data-mp="36oqa5paoiac4">
                  {t('+350 reseñas en Google', '+350 reviews on Google')}
                </p>
              </div>
            </div>

            <p className="text-gray-600 leading-relaxed mb-8" data-id="element-291" data-mp="3d2nj5pizi8br">
              {t('Más de 20 años recibiendo huéspedes en Dina Huapi, y la mejor carta de presentación son quienes ya se alojaron con nosotros.', 'Over 20 years hosting guests in Dina Huapi — and our best introduction is the people who have already stayed with us.')}
            </p>

            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-surface text-primary font-medium whitespace-nowrap hover:border-accent hover:text-accent transition-colors" data-id="element-292" data-mp="2ue8rd00ueq9w">
              {t('Ver todas en Google', 'See all on Google')}
              <ExternalLink className="w-4 h-4" data-id="element-293" data-mp="2blo2rus6f7bw" />
            </a>
          </motion.div>

          {/* Reseñas */}
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-294" data-mp="sr1obhihwo1f">
            {reviews.map((review, index) => <motion.figure key={review.name} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.35,
            delay: index * 0.06,
            ease: [0.23, 1, 0.32, 1]
          }} className="grid grid-rows-[auto_1fr_auto] bg-background rounded-2xl border border-surface/60 p-6" data-id="element-295" data-mp="oo5cqardhs6z" data-mpi={index}>
                <div className="flex items-center justify-between mb-4" data-id="element-296" data-mp="3s6oswews17lf">
                  <Stars rating={review.rating} data-id="element-297" data-mp="3s4lx1e8y0k8s" />
                  <span className="sr-only" data-id="element-298" data-mp="3n7viivcsdwph">
                    {t(`${review.rating} de 5 estrellas`, `${review.rating} out of 5 stars`)}
                  </span>
                  <span className="text-xs text-gray-400" data-id="element-299" data-mp="38jbz9ij01xgl">{review.date}</span>
                </div>

                <blockquote className="text-gray-700 leading-relaxed mb-6" data-id="element-300" data-mp="3dlkpw88egnfj">
                  {review.text}
                </blockquote>

                <figcaption className="flex items-center gap-3 pt-4 border-t border-surface" data-id="element-301" data-mp="33ltzahmymskq">
                  <span className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium shrink-0" aria-hidden="true" data-id="element-302" data-mp="3rbeh2x7dnjsf">
                    {review.initial}
                  </span>
                  <span className="text-sm font-medium text-primary" data-id="element-303" data-mp="3ih03r06eqjby">
                    {review.name}
                  </span>
                </figcaption>
              </motion.figure>)}
          </div>
        </div>
      </div>
    </section>;
}